import React from 'react';
import { BsArchive, BsBack, BsPencilSquare, BsTrash } from 'react-icons/bs';
import { Box } from '@chakra-ui/react';

import StackedButtonGroup from '../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import { inAppText } from '../../../constants/inAppText';
import { hideModal, showModal } from '../../../redux/modules/modal';
import { globalRequirementTemplateBuilder } from '../../../redux/reducer';
import ArchivePublishedTemplate from '../components/modals/ArchivePublishedTemplate';
import CreateUpdateFolderModal from '../components/modals/CreateUpdateFolderModal';
import DeleteTemplateFolderModal from '../components/modals/DeleteTemplateFolderModal';
import DraftDetailsModal from '../components/modals/DraftDetailsModal';
import ReopenArchivedTemplateModal from '../components/modals/ReopenArchivedTemplateModal';
import TemplateDetailsModal from '../components/modals/TemplateDetailsModal';

import editTemplate from './editTemplate';
import showDeleteTemplateModal from './showDeleteTemplateModal';

export const templateFolderOptions = [
  {
    label: 'Rename Folder',
    id: 'rename',
    icon: BsPencilSquare,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();

      const folder = e.currentTarget.value;
      const description = inAppText.templatesPage.folderMenu.renameExistingFolderModalMessage;

      dispatch(showModal(
        () => <CreateUpdateFolderModal description={description} folderId={folder} />,
        {
          title: 'Rename Folder',
          size: 'lg'
        }
      ));
    }
  },
  {
    label: 'Delete Folder',
    id: 'delete',
    icon: BsTrash,
    handler: ({ e, dispatch }) => {
      const folder = e.currentTarget.value;

      dispatch(showModal(
        () => <DeleteTemplateFolderModal folderId={folder} />,
        {
          title: 'Delete Folder?',
          size: 'md',
          buttons: {
            primaryButtonOptions: {
              isDisabled: true
            }
          }
        }
      ));
    }
  }
];

export const publishedTemplateOptions = [
  {
    label: 'Edit Template',
    id: 'edit',
    icon: BsPencilSquare,
    handler: editTemplate
  },
  {
    label: 'Template Details',
    id: 'details',
    icon: BsPencilSquare,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const templateId = Number(e.target.value);
      const description = inAppText.templatesPage.templateList.templateOptions.templateDetailsModalMessage;

      dispatch(showModal(
        () => <TemplateDetailsModal description={description} templateId={templateId} type="edit" />,
        {
          title: 'Template Details',
          size: 'lg'
        }
      ));
    }
  },
  {
    label: 'Duplicate Template',
    id: 'duplicate',
    icon: BsBack,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const templateId = Number(e.target.value);
      const description = inAppText.templatesPage.templateList.templateOptions.duplicateTemplateModalMessage;

      dispatch(showModal(
        () => <TemplateDetailsModal description={description} templateId={templateId} type="duplicate" />,
        {
          title: 'Duplicate Template',
          size: 'lg'
        }
      ));
    }
  },
  {
    label: 'Archive Template',
    id: 'archive',
    icon: BsArchive,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const templateId = Number(e.currentTarget.value);

      dispatch(showModal(
        () => <ArchivePublishedTemplate templateId={templateId} />,
        {
          title: 'Archive Template?',
          size: 'sm',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Archive',
              type: 'submit',
              form: 'archivePublishedTemplate'
            }
          }
        }
      ));
    }
  },
  {
    label: 'Delete Template',
    id: 'delete',
    icon: BsTrash,
    handler: showDeleteTemplateModal
  }
];

export const draftTemplateOptions = [
  {
    label: 'Edit Draft',
    id: 'edit',
    icon: BsPencilSquare,
    handler: async ({ e, dispatch, history }) => {
      e.preventDefault();
      e.stopPropagation();

      const templateId = Number(e.target.value);
      const currentCompanyId = Number(e.target.getAttribute('data-company-id'));
      await dispatch(globalRequirementTemplateBuilder.getRequirementTemplate(templateId));

      history.replace(
        `/companies/${currentCompanyId}/create-templates/requirement-templates/${templateId}`
      );
    }
  },
  {
    label: 'Draft Details',
    id: 'rename',
    icon: BsPencilSquare,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const templateId = Number(e.target.value);

      dispatch(showModal(
        () => <DraftDetailsModal templateId={templateId} />,
        {
          title: 'Draft Details',
          size: 'lg',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Save Draft',
              dataAnalyticsId: 'save-draft-details',
              type: 'submit',
              form: 'draftDetails'
            }
          }
        }
      ));
    }
  },
  {
    label: 'Delete Draft',
    id: 'delete',
    icon: BsTrash,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const templateId = Number(e.currentTarget.value);

      dispatch(showModal(
        () => <Box>This template will be gone forever.</Box>,
        {
          title: 'Delete Draft?',
          size: 'sm',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Delete',
              onClick: async () => {
                await dispatch(globalRequirementTemplateBuilder.deleteRequirementTemplate(templateId));
                dispatch(hideModal());
              }
            }
          }
        }
      ));
    }
  }
];

export const archivedTemplateOptions = [
  {
    label: 'Activate Template',
    id: 'edit',
    icon: BsPencilSquare,
    handler: ({ e, dispatch }) => {
      e.preventDefault();
      e.stopPropagation();
      const templateId = Number(e.currentTarget.value);

      dispatch(showModal(
        () => <ReopenArchivedTemplateModal templateId={templateId} />,
        {
          title: 'Reopen Archived Template',
          size: 'lg',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Reopen Template',
              type: 'submit',
              form: 'reopenArchivedTemplate',
              isDisabled: true
            }
          }
        }
      ));
    }
  },
  {
    label: 'Delete Template',
    id: 'delete',
    icon: BsTrash,
    handler: showDeleteTemplateModal
  }
];
